import React, { useCallback, useEffect, useState } from 'react'
import { Button, Divider, Row } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { Thinganator } from '@fs/zion-ui/quarks-authorized-use-only'
import { getOdmPersonDetails, getOdmPersonIdList, getOdmRecordResultCount } from '../../../../services/OdvService'
import useNotifications from '../../../../hooks/useNotifications'
import OdmRecordName from './OdmRecordName'
import OdmRecordEvents from './OdmRecordEvents'
import OdmRecordFamilies from './OdmRecordFamilies'
import OdmRecordNotes from './OdmRecordNotes'
import OdmRecordErrors from './OdmRecordErrors'
import OdmRecordOrdinances from './OdmRecordOrdinances'
import OdmRecordProxy from './OdmRecordProxy'
import OdmRecordBatch from './OdmRecordBatch'
import OdmRecordSubmitter from './OdmRecordSubmitter'
import OdmIndividualSource from './OdmIndividualSource'
import OdmAudits from './OdmAudits'

export default function OdmRecordsView({ odmRecordQuery }) {
  const PAGE_SIZE = 100

  const [query, setQuery] = useState()
  const [index, setIndex] = useState(0)
  const [page, setPage] = useState(0)
  const [lengthInfo, setLengthInfo] = useState({ length: 0, totalCountFound: false })
  const [nameIds, setNameIds] = useState()
  const [details, setDetails] = useState()
  const [resultReturned, setResultReturned] = useState(false)
  const [activeSections, setActiveSections] = useState({
    events: true,
    family: false,
    notes: false,
    errors: false,
    ords: true,
    proxy: false,
    batch: false,
    submitter: false,
    source: false,
    audit: false,
  })

  const { t } = useTranslation()
  const { error, ok } = useNotifications()

  const getTotalResultCount = useCallback(() => {
    getOdmRecordResultCount(query)
      .then((response) => {
        ok(t('odv.found.total', 'Found { count } total records', { count: response.data.recordCount }))
        setLengthInfo({ length: response.data.recordCount, totalCountFound: true })
      })
      .catch(() => {
        // do nothing. This will be called again periodically until a result is successfully returned
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  const handleFindingTotalCount = (responseLength) => {
    if (responseLength === 100) {
      const partialLength = (page + 1) * 100 + 1
      ok(t('odv.found', 'Found { count }+ records', { count: partialLength }))
      setLengthInfo({ ...lengthInfo, length: partialLength })
      getTotalResultCount()
    } else {
      const length = responseLength + lengthInfo.length
      ok(t('odv.found.total', 'Found { count } total records', { count: length }))
      setLengthInfo({ length, totalCountFound: true })
    }
  }

  function timeoutMessage() {
    error(
      t('odv.search.errors.timeout', {
        defaultValue: `Search timed out`,
        message: 'Search timed out. Please refine your search.',
      })
    )
  }

  const getNameIds = useCallback(() => {
    if (JSON.stringify(query) !== JSON.stringify(odmRecordQuery)) {
      setResultReturned(false)
      setQuery(odmRecordQuery)
      setPage(0)
      setIndex(0)
      setLengthInfo({ length: 0, totalCountFound: false })
    } else if (query) {
      getOdmPersonIdList(query, page)
        .then((response) => {
          if (response.data) {
            setNameIds(response.data)
            if (!lengthInfo.totalCountFound) {
              handleFindingTotalCount(response.data.length)
            }
          }
          setResultReturned(true)
        })
        .catch((err) => {
          if (err.response.status === 504) {
            timeoutMessage()
          } else {
            error(
              t('odv.search.errors.gettingOdmRecordSearchResults', {
                defaultValue: `Error getting ODM record search results: ${err.message || ''}`,
                message: err.message,
              }),
              err
            )
          }
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, query, odmRecordQuery])

  const getRecordDetails = useCallback(() => {
    if (nameIds && `${nameIds[index]}` !== details?.individual?.permanentRecFileNumber?.value) {
      getOdmPersonDetails(nameIds[index % PAGE_SIZE])
        .then((detailResponse) => {
          setDetails(detailResponse.data)
        })
        .catch((detailErr) => {
          if (detailErr.response.status === 504) {
            timeoutMessage()
          } else {
            error(
              t('odv.search.errors.gettingOdmRecordDetail', {
                defaultValue: `Error getting ODM record details search results: ${detailErr.message || ''}`,
                message: detailErr.message,
              }),
              detailErr
            )
          }
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameIds, index])

  useEffect(() => {
    getNameIds()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, query, odmRecordQuery])

  useEffect(() => {
    if (Math.trunc(index / 100) !== page) {
      setPage(Math.trunc(index / 100))
    } else if (nameIds && nameIds.length > index % PAGE_SIZE) {
      getRecordDetails()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameIds, index])

  return (
    <>
      {details && (
        <>
          <Divider marginY="nano" />
          <Row alignX="end">
            <Thinganator
              label={t('odm.record', 'Record')}
              index={index}
              setIndex={setIndex}
              length={lengthInfo.length}
            />
          </Row>
          <OdmRecordName individual={details.individual} collectionType={details?.extras?.nameSubmissionTypeId} />
          <Row>
            <Button
              emphasis={activeSections.events ? 'high' : 'medium'}
              onClick={() => setActiveSections({ ...activeSections, events: !activeSections.events })}
            >
              {t('odm.button.events', 'Events')}
            </Button>
            <Button
              emphasis={activeSections.family ? 'high' : 'medium'}
              onClick={() => setActiveSections({ ...activeSections, family: !activeSections.family })}
            >
              {t('odm.button.family', 'Family')}
            </Button>
            <Button
              emphasis={activeSections.notes ? 'high' : 'medium'}
              onClick={() => setActiveSections({ ...activeSections, notes: !activeSections.notes })}
            >
              {t('odm.button.notes', 'Notes')}
            </Button>
            <Button
              emphasis={activeSections.errors ? 'high' : 'medium'}
              onClick={() => setActiveSections({ ...activeSections, errors: !activeSections.errors })}
            >
              {t('odm.button.errors', 'Errors')}
            </Button>
            <Button
              emphasis={activeSections.ords ? 'high' : 'medium'}
              onClick={() => setActiveSections({ ...activeSections, ords: !activeSections.ords })}
            >
              {t('odm.button.ordinances', 'Ordinances')}
            </Button>
            <Button
              emphasis={activeSections.proxy ? 'high' : 'medium'}
              onClick={() => setActiveSections({ ...activeSections, proxy: !activeSections.proxy })}
            >
              {t('odm.button.proxy', 'Proxy')}
            </Button>
            <Button
              emphasis={activeSections.batch ? 'high' : 'medium'}
              onClick={() => setActiveSections({ ...activeSections, batch: !activeSections.batch })}
            >
              {t('odm.button.batch', 'Batch')}
            </Button>
            <Button
              emphasis={activeSections.submitter ? 'high' : 'medium'}
              onClick={() => setActiveSections({ ...activeSections, submitter: !activeSections.submitter })}
            >
              {t('odm.button.submitter', 'Submitter')}
            </Button>
            <Button
              emphasis={activeSections.source ? 'high' : 'medium'}
              onClick={() => setActiveSections({ ...activeSections, source: !activeSections.source })}
            >
              {t('odm.button.source', 'Source')}
            </Button>
            <Button
              emphasis={activeSections.audit ? 'high' : 'medium'}
              onClick={() => setActiveSections({ ...activeSections, audit: !activeSections.audit })}
            >
              {t('odm.button.audit', 'Audit')}
            </Button>
          </Row>
          {activeSections.events && <OdmRecordEvents events={details?.individual?.events} />}
          {activeSections.family && (
            <OdmRecordFamilies
              families={details?.extras?.families}
              collection={details?.extras?.nameSubmissionTypeId}
            />
          )}
          {activeSections.notes && <OdmRecordNotes notes={details?.extras?.notes} />}
          {activeSections.errors && <OdmRecordErrors errors={details?.extras?.errors} />}
          {activeSections.ords && <OdmRecordOrdinances ordinances={details?.individual?.ldsIndividualOrdinances} />}
          {activeSections.proxy && <OdmRecordProxy proxies={details?.extras?.proxies} />}
          {activeSections.batch && <OdmRecordBatch batch={details?.extras?.batch} />}
          {activeSections.submitter && <OdmRecordSubmitter submitter={details?.individual?.submitters?.[0]} />}
          {activeSections.source && <OdmIndividualSource source={details?.extras?.sources?.[0]} />}
          {activeSections.audit && <OdmAudits audits={details?.extras?.audits} />}
        </>
      )}
      {resultReturned && lengthInfo?.length === 0 && (
        <p id="odmRecordNoResults">{t('noResults', 'No Results Found')}</p>
      )}
    </>
  )
}
